import { useState } from "react";
import { useDispatch } from "react-redux";
import Input from "../../../../components/common/Input/Input";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import TelInput from "../../../../components/common/telInput/telInput";
import useDomesticHelperFlow from "../../../../hooks/useDomesticHelperFlowSimple";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useValidation from "../../../../hooks/useValidation";
import DomesticHelperBottomStickyBtnLayout from "../../layouts/DomesticHelperBottomStickyBtnLayout";
import DomesticHelperJourneyLayout from "../../layouts/DomesticHelperJourneyLayout";
import { updateHelperBasicInfo } from "../../../../redux/features/domesticHelperFlowSlice";

// Sample nationality options
const nationalityOptions = [
  { value: "PH", label: "Philippines", label_ar: "الفلبين" },
  { value: "IN", label: "India", label_ar: "الهند" },
  { value: "BD", label: "Bangladesh", label_ar: "بنغلاديش" },
  { value: "LK", label: "Sri Lanka", label_ar: "سريلانكا" },
  { value: "NP", label: "Nepal", label_ar: "نيبال" },
];

const genderOptions = [
  { value: "female", label: "Female", label_ar: "أنثى" },
  { value: "male", label: "Male", label_ar: "ذكر" },
];

const validationConfig = [
  {
    name: "fullName",
    validations: ["required", "length"],
    min: 2,
  },
  {
    name: "nationality",
    validations: ["required"],
  },
  {
    name: "passportNumber",
    validations: ["required", "length"],
    min: 6,
  },
  {
    name: "birthDate",
    validations: ["required"],
  },
  {
    name: "gender",
    validations: ["required"],
  },
  {
    name: "contactNumber",
    validations: ["length"],
    min: 8,
  },
];

const HelperBasicDetails = () => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const [values, setValues] = useState({});
  const { errors, validateFields } = useValidation();
  const { handleNextStep, handlePrevSubstep } = useDomesticHelperFlow();

  const validateForm = (data) => {
    return validateFields(validationConfig, data ? data : values);
  };

  const formSubmitHandler = (e) => {
    e.preventDefault();
    if (validateForm()) {
      dispatch(updateHelperBasicInfo(values));
      handleNextStep();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let update = { ...values, [name]: value };
    setValues(update);
    if (errors?.[name]) {
      validateForm(update);
    }
  };

  return (
    <div className="helper-basic-details w-full">
      <DomesticHelperJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
      >
        <div className="content min-w-[32%] max767:w-full">
          <h1 className="text-[#1A2024] text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] text-center max767:text-left">
            {translate("Helper Personal Details", "البيانات الشخصية للعاملة")}
          </h1>
          <p className="text-[#1A2024] text-base not-italic font-normal leading-6 tracking-[-0.16px] text-center mt-1 mb-6 max767:text-left">
            {translate(
              "Please provide the domestic helper's personal information",
              "يرجى تقديم المعلومات الشخصية للعاملة المنزلية"
            )}
          </p>
        </div>
        {/* onSubmit only covers pressing Enter in a field — the Continue button
            is portaled out of this form's DOM subtree, so its click has to be
            wired explicitly. Same reason as the start step's basic info. */}
        <form onSubmit={formSubmitHandler} className="helper-form w-full">
          <DomesticHelperBottomStickyBtnLayout
            show_summary={false}
            btn_onClick={formSubmitHandler}
          >
            <div className="w-[32%] max767:w-full mx-auto ipad:w-[60%]">
              <Input
                label={translate("Full Name", "الاسم الكامل")}
                type="text"
                name="fullName"
                value={values.fullName || ""}
                error={errors.fullName}
                handleChange={handleChange}
                placeholder={translate(
                  "Enter helper's full name",
                  "أدخل الاسم الكامل للعاملة"
                )}
              />

              <SelectInput
                name="nationality"
                options_data={nationalityOptions}
                formData={values}
                default_placeholder={translate(
                  "Select Nationality",
                  "اختر الجنسية"
                )}
                error={errors?.nationality}
                handleChange={handleChange}
                label={translate("Nationality", "الجنسية")}
              />

              <Input
                label={translate("Passport Number", "رقم جواز السفر")}
                type="text"
                name="passportNumber"
                value={values.passportNumber || ""}
                error={errors.passportNumber}
                handleChange={handleChange}
                placeholder={translate(
                  "Enter passport number",
                  "أدخل رقم جواز السفر"
                )}
              />

              <Input
                label={translate("Date of Birth", "تاريخ الميلاد")}
                type="date"
                name="birthDate"
                value={values.birthDate || ""}
                error={errors.birthDate}
                handleChange={handleChange}
              />

              <SelectInput
                name="gender"
                options_data={genderOptions}
                formData={values}
                default_placeholder={translate("Select Gender", "اختر الجنس")}
                error={errors?.gender}
                handleChange={handleChange}
                label={translate("Gender", "الجنس")}
              />

              <TelInput
                name="contactNumber"
                value={values.contactNumber || ""}
                error={errors.contactNumber}
                onChange={handleChange}
                label={translate("Contact Number", "رقم الاتصال")}
                className="mobilenumber px-4 py-3 max767:w-[50%] w-[97%] border-r-0 border-0 rounded-[9px] h-[46px] focus:outline-none"
              />
            </div>
          </DomesticHelperBottomStickyBtnLayout>
        </form>
      </DomesticHelperJourneyLayout>
    </div>
  );
};

export default HelperBasicDetails;
